// CodeMirror 6 entry point for st-sprite studio. Bundled by esbuild into
// `assets/js/studio.bundle.js` (served by cargo-leptos at /js/...). Exposes
// `window.StSpriteStudio.mountEditor(parent, opts)`; the Rust shim in
// `crates/app/src/studio/codemirror.rs` calls this through wasm-bindgen.

import { EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { bracketMatching, indentOnInput } from '@codemirror/language';
import { lintGutter, setDiagnostics } from '@codemirror/lint';

import { forth } from './forth-mode.js';

/**
 * Mount a CodeMirror 6 editor into `parent`.
 *
 * @param {HTMLElement} parent - container element to mount into.
 * @param {Object} [options]
 * @param {string} [options.initialDoc] - initial source.
 * @param {(doc: string) => void} [options.onChange] - debounced doc-change callback (250ms).
 * @returns {{ getDoc(): string, setDoc(s: string): void, destroy(): void }} handle.
 */
export function mountEditor(parent, options = {}) {
    const { initialDoc = '', onChange } = options;

    let onChangeTimer = null;
    const debouncedOnChange = (view) => {
        if (!onChange) return;
        if (onChangeTimer) clearTimeout(onChangeTimer);
        onChangeTimer = setTimeout(() => {
            onChange(view.state.doc.toString());
        }, 250);
    };

    const view = new EditorView({
        state: EditorState.create({
            doc: initialDoc,
            extensions: [
                lineNumbers(),
                history(),
                bracketMatching(),
                indentOnInput(),
                highlightActiveLine(),
                lintGutter(),
                keymap.of([...defaultKeymap, ...historyKeymap]),
                ...forth(),
                EditorView.updateListener.of((update) => {
                    if (update.docChanged) debouncedOnChange(update.view);
                }),
            ],
        }),
        parent,
    });

    return {
        getDoc: () => view.state.doc.toString(),
        setDoc: (s) => {
            view.dispatch({
                changes: { from: 0, to: view.state.doc.length, insert: s },
            });
        },
        /**
         * Replace the editor's lint diagnostics. `diags` is an array of
         * `{ line: number, message: string, severity: 'error'|'warning'|'info' }`.
         * Lines are 1-indexed; columns aren't supported (the lexer only
         * tracks line numbers, P2.1.5).
         */
        setDiagnostics: (diags) => {
            const cmDiags = (diags || []).flatMap((d) => {
                const lineNum = Math.max(1, Math.min(view.state.doc.lines, d.line | 0));
                const lineObj = view.state.doc.line(lineNum);
                return [{
                    from: lineObj.from,
                    to: lineObj.to,
                    severity: d.severity || 'error',
                    message: d.message || '',
                }];
            });
            view.dispatch(setDiagnostics(view.state, cmDiags));
        },
        destroy: () => view.destroy(),
    };
}
